import * as XLSX from 'xlsx';
import { downloadBlob, zipFiles } from './zipFiles';
import { isItalianHoliday } from './holidays';
import { InpasPresenceValidation } from './inpasExport';
import { formatValidationSentence } from './entryGate';

export interface StandardLeaveSummary {
  type: string;
  hours: number | null;
  leave_reason?: string | null;
}

export interface StandardDayRecord {
  date: string;
  dayOfWeek: string;
  check_in1: string | null;
  check_out1: string | null;
  check_in2: string | null;
  check_out2: string | null;
  total_hours: number | null;
  status: 'complete' | 'incomplete' | 'holiday' | 'absence' | 'leave';
  notes: string | null;
  leaves?: StandardLeaveSummary[];
  expectedHours?: number | null;
  lastAdminUpdateAt?: string;
  lastAdminFullName?: string;
  lastAdminNote?: string | null;
  wasModified?: boolean;
}

interface StandardTimesheetEmployeeInfo {
  fullName: string;
  matricola?: string | null;
  dipartimento?: string | null;
  weeklyHours?: number | null;
  weeklyDays?: number | null;
  lunchBreakMinutes?: number | null;
  validationSentence?: string | null;
}

interface StandardTimesheetHistoryResponse {
  history?: any[];
  leaves?: any[];
  expected_hours?: number | null;
  expected_hours_per_day?: Record<string, unknown>;
}

const monthNames = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

const cleanFileName = (value: string) => value.replace(/[\\\/?*\[\]:<>|"]/g, '_').trim();

const buildLeaveMap = (leaves: any[]): Map<string, StandardLeaveSummary[]> => {
  const leaveMap = new Map<string, StandardLeaveSummary[]>();
  leaves.forEach((entry: any) => {
    const rawDate: string | undefined = entry?.absence_date || entry?.absenceDate;
    if (!rawDate || typeof rawDate !== 'string') {
      return;
    }

    const dateKey = rawDate.split('T')[0];
    const rawType = entry?.absence_type || entry?.absenceType || '';
    const hoursValue = entry?.hours_absent ?? entry?.hoursAbsent ?? null;

    let hours: number | null = null;
    if (typeof hoursValue === 'number') {
      hours = hoursValue;
    } else if (typeof hoursValue === 'string') {
      const parsed = parseFloat(hoursValue);
      if (!Number.isNaN(parsed)) {
        hours = parsed;
      }
    }

    const leaveReason = entry?.leave_reason || entry?.leaveReason || null;
    const summary: StandardLeaveSummary = {
      type: String(rawType),
      hours,
      leave_reason: leaveReason,
    };

    const list = leaveMap.get(dateKey) ?? [];
    list.push(summary);
    leaveMap.set(dateKey, list);
  });

  return leaveMap;
};

const buildExpectedDailyMap = (raw: Record<string, unknown> | undefined): Record<string, number> => {
  const result: Record<string, number> = {};
  if (!raw || typeof raw !== 'object') {
    return result;
  }

  Object.entries(raw).forEach(([key, value]) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      result[key] = value;
      return;
    }

    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!Number.isNaN(parsed)) {
        result[key] = parsed;
      }
    }
  });

  return result;
};

const buildDayRecords = (
  selectedYear: number,
  selectedMonth: number,
  history: any[],
  leaves: any[],
  expectedDaily: Record<string, number>
): StandardDayRecord[] => {
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const dayRecords: StandardDayRecord[] = [];
  const leaveMap = buildLeaveMap(leaves);

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(selectedYear, selectedMonth - 1, day);
    const dayOfWeek = dayNames[date.getDay()];
    const isHoliday = isItalianHoliday(date);

    const presence = history.find((item: any) => {
      const checkIn = new Date(item.check_in);
      return (
        checkIn.getDate() === day &&
        checkIn.getMonth() === selectedMonth - 1 &&
        checkIn.getFullYear() === selectedYear
      );
    });

    const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const leaveSummaries = leaveMap.get(dateKey) || [];

    let status: StandardDayRecord['status'] = isHoliday ? 'holiday' : 'absence';
    let checkIn1: string | null = null;
    let checkOut1: string | null = null;
    let checkIn2Time: string | null = null;
    let checkOut2Time: string | null = null;
    let totalHours: number | null = null;
    let lastAdminUpdateAt: string | undefined;
    let lastAdminFullName: string | undefined;
    let lastAdminNote: string | null | undefined;

    const modificationsRaw = Array.isArray(presence?.modifications) ? presence.modifications : [];

    if (presence) {
      checkIn1 = presence.check_in
        ? new Date(presence.check_in).toLocaleTimeString('it-IT', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : null;
      checkOut1 = presence.check_out
        ? new Date(presence.check_out).toLocaleTimeString('it-IT', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : null;
      checkIn2Time = presence.check_in_2
        ? new Date(presence.check_in_2).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        : null;
      checkOut2Time = presence.check_out_2
        ? new Date(presence.check_out_2).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        : null;

      const numericWorkHours =
        typeof presence.work_hours === 'number'
          ? presence.work_hours
          : presence.work_hours != null
          ? Number(presence.work_hours)
          : null;
      totalHours =
        numericWorkHours != null && !Number.isNaN(numericWorkHours)
          ? numericWorkHours
          : presence.total_hours ?? 0;

      if (presence.check_out) {
        status = isHoliday ? 'holiday' : 'complete';
      } else if (presence.check_in) {
        status = 'incomplete';
      }

      if (presence.last_admin_update_at) {
        lastAdminUpdateAt = presence.last_admin_update_at;
      }

      const adminNameParts = [presence.last_admin_first_name, presence.last_admin_last_name].filter(Boolean);
      if (adminNameParts.length) {
        lastAdminFullName = adminNameParts.join(' ');
      }

      lastAdminNote = presence.last_admin_note ?? null;
    }

    const expectedDayHours =
      typeof expectedDaily[dateKey] === 'number' ? expectedDaily[dateKey] : null;

    const permissionHours = leaveSummaries
      .filter((leave) => leave.type === 'permesso' || leave.type === 'ex_festivita')
      .reduce((sum, leave) => sum + (leave.hours || 0), 0);

    // Il permesso non va sommato sopra l'orario già timbrato: scala le ore di
    // presenza conteggiate con tetto alle ore attese del giorno (al netto del
    // permesso), coerentemente col motore di calcolo del backend. Es. timbratura
    // 8h + permesso 2h con 8h attese -> 6h presenza + 2h permesso = 8h totali.
    let countedPresence = totalHours || 0;
    if (typeof expectedDayHours === 'number' && expectedDayHours > 0) {
      const maxPresence = Math.max(0, expectedDayHours - permissionHours);
      if (countedPresence > maxPresence) {
        countedPresence = maxPresence;
      }
    }
    const totalDayHours = countedPresence + permissionHours;

    if (leaveSummaries.length > 0 && presence == null && !isHoliday) {
      status = 'leave';
    }

    dayRecords.push({
      date: dateKey,
      dayOfWeek,
      check_in1: checkIn1,
      check_out1: checkOut1,
      check_in2: checkIn2Time,
      check_out2: checkOut2Time,
      total_hours: totalDayHours,
      status,
      notes: presence?.notes || null,
      leaves: leaveSummaries.length > 0 ? leaveSummaries : undefined,
      expectedHours: expectedDayHours,
      lastAdminUpdateAt,
      lastAdminFullName,
      lastAdminNote,
      wasModified: Boolean(lastAdminUpdateAt || modificationsRaw.length > 0),
    });
  }

  return dayRecords;
};

const buildWorkbook = (
  employeeInfo: StandardTimesheetEmployeeInfo,
  records: StandardDayRecord[],
  year: number,
  month: number,
  expectedHours: number | null
) => {
  const totalHours = records.reduce((sum, record) => sum + (record.total_hours || 0), 0);

  const exportData = records.map((record) => {
    const dayOfMonth = Number(record.date.split('-')[2] || '0');
    let leavesInfo = '';
    if (record.leaves && record.leaves.length > 0) {
      leavesInfo = record.leaves
        .map((leave) => {
          const labels: Record<string, string> = {
            ferie: 'Ferie',
            permesso: 'Permesso',
            malattia: 'Malattia',
            ex_festivita: 'Ex Festività',
            altri: 'Altri',
          };
          return `${labels[leave.type] || leave.type}: ${leave.hours || 0}h`;
        })
        .join(', ');
    }

    let statusText = '';
    switch (record.status) {
      case 'complete':
        statusText = 'Completo';
        break;
      case 'incomplete':
        statusText = 'Incompleto';
        break;
      case 'holiday':
        statusText = 'Festivo';
        break;
      case 'leave':
        statusText = 'Ferie/Permesso';
        break;
      case 'absence':
        statusText = 'Assenza';
        break;
      default:
        statusText = '-';
    }

    return {
      Giorno: dayOfMonth || '-',
      'Giorno Settimana': record.dayOfWeek,
      Data: record.date,
      'Entrata 1': record.check_in1 || '-',
      'Uscita 1': record.check_out1 || '-',
      'Entrata 2': record.check_in2 || '-',
      'Uscita 2': record.check_out2 || '-',
      'Ore Totali': record.total_hours != null ? record.total_hours.toFixed(2) : '-',
      'Ore Attese': record.expectedHours != null ? record.expectedHours.toFixed(2) : '-',
      Stato: statusText,
      'Ferie/Permessi': leavesInfo || '-',
      Note: record.notes || '-',
      Modificato: record.wasModified ? 'Si' : 'No',
      'Ultima Modifica': record.lastAdminUpdateAt
        ? new Date(record.lastAdminUpdateAt).toLocaleString('it-IT')
        : '-',
      'Modificato da': record.lastAdminFullName || '-',
      'Note Admin': record.lastAdminNote || '-',
    };
  });

  const summaryRow = {
    Giorno: 'RIEPILOGO',
    'Giorno Settimana': '',
    Data: '',
    'Entrata 1': '',
    'Uscita 1': '',
    'Entrata 2': '',
    'Uscita 2': '',
    'Ore Totali': totalHours.toFixed(2),
    'Ore Attese': expectedHours != null ? expectedHours.toFixed(2) : '-',
    Stato: '',
    'Ferie/Permessi': '',
    Note: '',
    Modificato: '',
    'Ultima Modifica': '',
    'Modificato da': '',
    'Note Admin': '',
  };

  const ws = XLSX.utils.json_to_sheet([...exportData, summaryRow]);
  const wb = XLSX.utils.book_new();

  ws['!cols'] = [
    { wch: 8 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 12 },
    { wch: 12 },
    { wch: 15 },
    { wch: 20 },
    { wch: 30 },
    { wch: 10 },
    { wch: 20 },
    { wch: 20 },
    { wch: 30 },
  ];

  const employeeSheet = [
    ['DIPENDENTE', employeeInfo.fullName],
    ['Matricola', employeeInfo.matricola || '-'],
    ['Dipartimento', employeeInfo.dipartimento || '-'],
    ['Mese', `${monthNames[month - 1]} ${year}`],
    ['Ore Settimanali', employeeInfo.weeklyHours ?? '-'],
    ['Giorni Settimanali', employeeInfo.weeklyDays ?? '-'],
    ['Pausa Pranzo (min)', employeeInfo.lunchBreakMinutes ?? '-'],
    [''],
    ['RIEPILOGO MENSILE'],
    ['Totale Ore', totalHours.toFixed(2)],
    ['Giorni Lavorativi', records.filter((record) => record.status === 'complete').length],
    ['Ore Attese', expectedHours != null ? expectedHours.toFixed(2) : '-'],
    [''],
    ...(employeeInfo.validationSentence ? [[employeeInfo.validationSentence, '']] : []),
  ];

  const infoWs = XLSX.utils.aoa_to_sheet(employeeSheet);
  XLSX.utils.book_append_sheet(wb, infoWs, 'Info Dipendente');
  XLSX.utils.book_append_sheet(wb, ws, 'Cartellino');
  return wb;
};

const downloadWorkbook = (workbook: XLSX.WorkBook, filename: string) => {
  XLSX.writeFile(workbook, filename);
};

export const downloadMassiveStandardExport = async (
  validations: InpasPresenceValidation[],
  year: number,
  month: number,
  loadHistory: (
    validation: InpasPresenceValidation,
    startDate: string,
    endDate: string
  ) => Promise<StandardTimesheetHistoryResponse>
) => {
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  let successCount = 0;
  let failureCount = 0;
  const failedEmployees: string[] = [];
  const files: { name: string; data: Uint8Array }[] = [];

  for (let index = 0; index < validations.length; index++) {
    const validation = validations[index];
    try {
      const historyResponse = await loadHistory(validation, startDate, endDate);
      const history = Array.isArray(historyResponse.history) ? historyResponse.history : [];
      const leaves = Array.isArray(historyResponse.leaves) ? historyResponse.leaves : [];
      const expectedDaily = buildExpectedDailyMap(historyResponse.expected_hours_per_day);
      const records = buildDayRecords(year, month, history, leaves, expectedDaily);
      const expectedHours =
        typeof historyResponse.expected_hours === 'number'
          ? historyResponse.expected_hours
          : records.reduce((sum, record) => sum + (record.expectedHours || 0), 0);

      const fullName = `${validation.employee_surname || ''} ${validation.employee_name || ''}`.trim();
      const employeeInfo: StandardTimesheetEmployeeInfo = {
        fullName,
        matricola: validation.employee_matricola || null,
        dipartimento: validation.employee_dipartimento || null,
        weeklyHours: validation.employee_weekly_hours ?? null,
        weeklyDays: validation.employee_weekly_days ?? null,
        lunchBreakMinutes: validation.employee_lunch_break_minutes ?? null,
        validationSentence: validation.status === 'revoked'
          ? null
          : formatValidationSentence(validation.validator_name, validation.validator_surname, validation.validated_at),
      };

      const workbook = buildWorkbook(employeeInfo, records, year, month, expectedHours);
      const filename = `Cartellino_${cleanFileName(fullName)}_${monthNames[month - 1]}_${year}.xlsx`;
      files.push({
        name: filename,
        data: new Uint8Array(XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })),
      });
      successCount++;
    } catch (error) {
      failureCount++;
      failedEmployees.push(`${validation.employee_surname || ''} ${validation.employee_name || ''}`.trim());
    }

  }

  if (files.length > 0) {
    downloadBlob(zipFiles(files), `Cartellini_${monthNames[month - 1]}_${year}.zip`);
  }

  return {
    successCount,
    failureCount,
    failedEmployees,
  };
};
